/**
 * The key a conversation is created and found by (`draftKey`, ai-plan-builder R12).
 *
 * ── One key per CONVERSATION, since the module has a history (2 October 2026) ────────────────
 * Every conversation is created under a key of its own. What this device keeps is the key of the
 * conversation the trainer is IN for a recipient: the slot `movebuddy.aiDraftKey.<userId>.<recipient>`.
 * „Nowa rozmowa" puts a fresh key in the slot (`newConversationKey`) and leaves the conversation
 * under the earlier key alone: it is on the server and in the history, where it is opened by its
 * id. The slot is the one wave 1 wrote (then: one conversation per recipient), read unchanged, so
 * a conversation started on wave 1 is still the one this device resumes.
 *
 * Minted once and kept per trainer AND per recipient, so a reload or a tab closed while Buddy works
 * finds the same conversation and resumes watching it, and a conversation started for one client
 * never opens for another (or for the library). It is let go once the conversation is bound to the
 * template its draft became: seen by the page on the conversation's next read, or told by the
 * assignment the moment its bind answers (a client's own copy is recorded without being linked,
 * so no later read would ever show it bound). The next visit starts a new conversation.
 *
 * A key written under the earlier per-trainer name (`movebuddy.aiDraftKey.<userId>`) is never read:
 * it cannot say which recipient it was for, so it is ignored rather than guessed at.
 *
 * Pure: the store is passed in, and a value that does not look like a key this build writes is
 * ignored rather than trusted (an older build, a hand edit, another app on the origin).
 */

import type { ForgettingPreferenceStore } from '~/shared/lib/preferenceStore';

/** The backend accepts 1 to 64 characters; this build writes UUIDs. */
const KEY_SHAPE = /^[A-Za-z0-9-]{8,64}$/;

/** Whose plan the conversation builds: a client's id, or `null` for the trainer's own library. */
export type DraftRecipient = number | null;

function storageKey(userId: string, recipient: DraftRecipient): string {
  return `movebuddy.aiDraftKey.${userId}.${recipient === null ? 'library' : String(recipient)}`;
}

/** The trainer's current draft key, minting and keeping a new one when there is none to reuse. */
export function currentDraftKey(
  store: ForgettingPreferenceStore | null,
  userId: string,
  recipient: DraftRecipient,
  mint: () => string,
): string {
  let stored: string | null;
  try {
    stored = store?.getItem(storageKey(userId, recipient)) ?? null;
  } catch {
    stored = null;
  }
  if (stored !== null && KEY_SHAPE.test(stored)) return stored;
  const minted = mint();
  try {
    store?.setItem(storageKey(userId, recipient), minted);
  } catch {
    // Storage refused: the conversation lives for this visit only, which is all it can do.
  }
  return minted;
}

/** Let the key go once its conversation is bound to a saved template, so the next visit starts afresh. */
export function forgetDraftKey(
  store: ForgettingPreferenceStore | null,
  userId: string,
  recipient: DraftRecipient,
  key: string,
): void {
  try {
    if (store?.getItem(storageKey(userId, recipient)) === key) store.removeItem(storageKey(userId, recipient));
  } catch {
    // Nothing to forget in a store that cannot be read.
  }
}

/**
 * Let the recipient's key go whatever it holds: the assignment's bind answered, and the key is the
 * one the assigned draft was opened from (a key changes only when its conversation is bound, so
 * no newer conversation can stand under it yet).
 */
export function forgetRecipientDraftKey(
  store: ForgettingPreferenceStore | null,
  userId: string,
  recipient: DraftRecipient,
): void {
  try {
    store?.removeItem(storageKey(userId, recipient));
  } catch {
    // Nothing to forget in a store that cannot be written.
  }
}

/**
 * „Nowa rozmowa" (and a client's „Plan z Buddym"): a fresh key for the recipient's slot. The
 * conversation under the earlier key is not touched; it stays in the history.
 */
export function newConversationKey(
  store: ForgettingPreferenceStore | null,
  userId: string,
  recipient: DraftRecipient,
  mint: () => string,
): string {
  forgetRecipientDraftKey(store, userId, recipient);
  return currentDraftKey(store, userId, recipient, mint);
}
